import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, Snowflake, CloudLightning, Newspaper, MapPin, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { useRest, RCard } from './restui';
import { trackEvent } from '../../lib/analytics';

/* Local context & industry intelligence — same component for demo and every tenant. */

const API = process.env.REACT_APP_BACKEND_URL;

export const wIcon = (code, cls = 'w-6 h-6') => {
  if (code === 0) return <Sun className={`${cls} text-amber-500`} />;
  if (code <= 2) return <CloudSun className={`${cls} text-amber-400`} />;
  if (code === 3) return <Cloud className={`${cls} text-gray-400`} />;
  if (code <= 48) return <CloudFog className={`${cls} text-gray-400`} />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain className={`${cls} text-sky-500`} />;
  if (code <= 77) return <Snowflake className={`${cls} text-sky-300`} />;
  return <CloudLightning className={`${cls} text-violet-500`} />;
};

export const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay }} className={className}>
    {children}
  </motion.div>
);

function LocalNews({ p, isEs }) {
  if (!p.local_news?.length) return null;
  return (
    <RCard className="p-6" testid="rest-local-news">
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'Noticias locales' : 'Local news'} · {p.city}</p>
      <div className="mt-2 divide-y divide-gray-100">
        {p.local_news.slice(0, 5).map((n, i) => (
          <a key={i} href={`/go?u=${encodeURIComponent(n.url)}`} target="_blank" rel="noopener noreferrer" className="group block py-2.5">
            <p className="text-[13px] font-semibold text-gray-800 leading-snug group-hover:text-[var(--rest-accent)] transition-colors">{n.title}</p>
            <p className="mt-0.5 text-[11px] text-gray-400">{n.source} {n.date && `· ${n.date}`}</p>
          </a>
        ))}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-wider text-gray-300 font-bold">{isEs ? 'Fuente: Google News en vivo' : 'Source: Live Google News'}</p>
    </RCard>
  );
}

function AreaMap({ p, isEs }) {
  if (!p.map_q) return null;
  return (
    <RCard className="p-0 overflow-hidden flex flex-col" testid="rest-area-map">
      <p className="px-6 pt-6 text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5 text-[var(--rest-accent)]" /> {isEs ? 'Competencia cercana' : 'Nearby competitors'}
      </p>
      <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(p.map_q)}&output=embed`}
        className="mt-3 w-full flex-1 min-h-[240px] border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
    </RCard>
  );
}

function IndustryPulse({ isEs }) {
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/intelligence-feed?limit=12`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFeed((d?.items || []).filter((it) => (!it.lang || it.lang === (isEs ? 'es' : 'en')) && /^https?:\/\//.test(it.url || '')).slice(0, 4)))
      .catch(() => {});
  }, [isEs]);
  if (!feed.length) return null;
  return (
    <RCard className="p-6" testid="rest-industry-pulse">
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold flex items-center gap-1.5">
        <Newspaper className="w-3.5 h-3.5 text-[var(--rest-accent)]" /> Industry Pulse
      </p>
      <div className="mt-2 divide-y divide-gray-100">
        {feed.map((it) => (
          <a key={it.id} href={`/go?u=${encodeURIComponent(it.url)}`} target="_blank" rel="noopener noreferrer" className="group block py-2.5">
            <p className="text-[13px] font-semibold text-gray-800 leading-snug group-hover:text-[var(--rest-accent)] transition-colors">
              {isEs ? (it.title_es || it.title) : it.title}
            </p>
          </a>
        ))}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-wider text-gray-300 font-bold">{isEs ? 'Fuente: Kitxens Intelligence' : 'Source: Kitxens Intelligence'}</p>
    </RCard>
  );
}

const WEEKLY_TASKS = [
  ['Responde todas las reseñas nuevas de esta semana', 'Reply to all new reviews this week'],
  ['Revisa que tu menú y horarios estén al día en Google', 'Check your menu and hours are current on Google'],
  ['Publica 2 posts con tu plato estrella en redes', 'Post twice featuring your star dish on social'],
  ['Mira qué local o canal vendió menos y pregúntale a Rachel', 'Check which location or channel sold less and ask Rachel'],
  ['Agradece a tus clientes que repiten (email o en sala)', 'Thank your repeat guests (email or in person)'],
];

function WeeklyChecklist({ isEs }) {
  const wk = `${new Date().getFullYear()}-w${Math.ceil(((new Date() - new Date(new Date().getFullYear(), 0, 1)) / 86400000 + 1) / 7)}`;
  const key = `kx_weekly_${wk}`;
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } });
  const toggle = (i) => {
    const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
    setDone(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* noop */ }
  };
  return (
    <RCard className="p-6" testid="rest-weekly-checklist">
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-[var(--rest-accent)]" /> {isEs ? 'Checklist de la semana' : 'This week\u2019s checklist'}
      </p>
      <p className="mt-1 text-[11.5px] text-gray-400">{isEs ? `${done.length}/${WEEKLY_TASKS.length} completadas · se reinicia cada semana` : `${done.length}/${WEEKLY_TASKS.length} done · resets every week`}</p>
      <div className="mt-2.5 space-y-1">
        {WEEKLY_TASKS.map((t, i) => (
          <button key={i} onClick={() => toggle(i)} data-testid={`rest-weekly-task-${i}`}
            className="w-full flex items-start gap-2.5 py-1.5 text-left group">
            <span className={`mt-0.5 w-[18px] h-[18px] shrink-0 rounded-full border-2 flex items-center justify-center text-[10px] font-black transition-all ${done.includes(i) ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 text-transparent group-hover:border-emerald-400'}`}>✓</span>
            <span className={`text-[13px] leading-snug transition-colors ${done.includes(i) ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{t[isEs ? 0 : 1]}</span>
          </button>
        ))}
      </div>
    </RCard>
  );
}

const MARBLISM_URL = 'https://marblism.com?via=kitxens-com';

function MarblismCard({ isEs }) {
  return (
    <RCard className="p-6 relative overflow-hidden" testid="rest-marblism-card">
      <span className="absolute inset-x-0 top-0 h-[3px] bg-[var(--rest-accent,#F77F11)]" aria-hidden="true" />
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'Recomendado por Kitxens' : 'Recommended by Kitxens'}</p>
      <p className="mt-1.5 text-[14.5px] font-bold text-gray-900">{isEs ? 'Empleados IA para tu restaurante, con Marblism' : 'AI employees for your restaurant, with Marblism'}</p>
      <p className="mt-1.5 text-[12.5px] text-gray-500 leading-relaxed">
        {isEs
          ? 'La plataforma que usamos para crear agentes IA que contestan emails, llamadas y redes por ti, 24/7. Es la base sobre la que tu equipo Kitxens trabaja cada día.'
          : 'The platform we use to build AI agents that answer your emails, calls and social 24/7. It\u2019s the foundation your Kitxens team works on every day.'}
      </p>
      <a href={MARBLISM_URL} target="_blank" rel="noopener noreferrer" data-testid="rest-marblism-cta"
        onClick={() => trackEvent('partner_click', { kind: 'marblism', slug: 'rest-home', url: MARBLISM_URL })}
        className="mt-3.5 inline-flex items-center gap-1.5 rounded-full bg-gray-900 hover:bg-black text-white px-5 py-2 text-[12.5px] font-bold transition-colors">
        {isEs ? 'Probar Marblism' : 'Try Marblism'} <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
      <p className="mt-2 text-[10px] text-gray-400">{isEs ? 'Enlace de partner — a ti te cuesta lo mismo y apoyas a Kitxens.' : 'Partner link — same price for you, and it supports Kitxens.'}</p>
    </RCard>
  );
}

export function PulseSection({ isEs }) {
  const { data: p } = useRest('/pulse');
  if (!p) return null;
  const hasAny = p.local_news?.length || p.map_q;
  if (!hasAny) return null;
  return (
    <Reveal className="mt-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <IndustryPulse isEs={isEs} />
          <WeeklyChecklist isEs={isEs} />
          <MarblismCard isEs={isEs} />
        </div>
        <div className="space-y-4">
          <LocalNews p={p} isEs={isEs} />
          <AreaMap p={p} isEs={isEs} />
        </div>
      </div>
    </Reveal>
  );
}
